/**
 * Properties-filter model for the comparison results page (mock v16).
 *
 * The "Add Filter" modal builds filters that narrow the Properties list. Filters
 * combine with AND; a filter with no values selected simply passes every
 * property. Most categorical filters key on the same attributes as Color by
 * Attribute, so that value logic is shared; Slot ID is a filter-only attribute
 * with its own accessor (see below).
 */
import { buildAttributeRows, type EconomicsPair } from './property-attributes';
import {
  attrValues,
  attrValueLabel,
  buildAttrValueOptions,
  type ColorByAttrKey,
} from './color-by';
import type { ComparisonResult } from './types';

export type FilterKey =
  | 'difference'
  | 'slotId'
  | 'resCategory'
  | 'status'
  | 'volumeSource'
  | 'assignedUnit';

export type FilterKind = 'difference' | 'categorical';

export interface FilterDef {
  key: FilterKey;
  label: string;
  kind: FilterKind;
}

/** The filters offered in the modal's list, in order. */
export const FILTER_DEFS: FilterDef[] = [
  { key: 'difference', label: 'Difference Filter', kind: 'difference' },
  { key: 'slotId', label: 'Slot ID', kind: 'categorical' },
  { key: 'resCategory', label: 'Reserve Category', kind: 'categorical' },
  { key: 'status', label: 'Status', kind: 'categorical' },
  { key: 'volumeSource', label: 'Volume Source', kind: 'categorical' },
  { key: 'assignedUnit', label: 'Assigned Unit', kind: 'categorical' },
];

/** One active/staged filter — the checked difference fields / categorical values. */
export interface PropertyFilter {
  id: string;
  key: FilterKey;
  label: string;
  kind: FilterKind;
  enabled: boolean;
  /** difference: attribute-row keys; categorical: canonical value keys. */
  values?: string[];
}

// Most categorical filter keys are a subset of the color-by attribute keys.
const CATEGORICAL_ATTR: Record<string, ColorByAttrKey> = {
  resCategory: 'resCategory',
  status: 'status',
  volumeSource: 'volumeSource',
  assignedUnit: 'assignedUnit',
};

/**
 * A categorical filter's value logic: the canonical values a property carries,
 * the display label for a value, and the modal's option list. Color-by-backed
 * filters delegate to the shared color-by accessors; Slot ID is a filter-only
 * attribute (the property's unique slot id — unique per property, so it is never
 * offered as a Color-by attribute) and defines its logic locally.
 */
interface CategoricalAccessor {
  values: (result: ComparisonResult) => string[];
  label: (value: string) => string;
  options: (results: ComparisonResult[]) => { value: string; label: string }[];
}

const slotIdAccessor: CategoricalAccessor = {
  values: (r) => (r.uniqueSlotId ? [r.uniqueSlotId] : []),
  label: (v) => v,
  options: (results) => {
    const ids = new Set<string>();
    results.forEach((r) => {
      if (r.uniqueSlotId) ids.add(r.uniqueSlotId);
    });
    return Array.from(ids)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  },
};

// The value logic for a categorical filter key: Slot ID's local accessor, or a
// color-by-backed accessor for the rest.
const categoricalAccessor = (key: FilterKey): CategoricalAccessor => {
  if (key === 'slotId') return slotIdAccessor;
  const attr = CATEGORICAL_ATTR[key];
  return {
    values: (r) => attrValues(r, attr),
    label: (v) => attrValueLabel(attr, v),
    options: (results) => buildAttrValueOptions(results, attr),
  };
};

let idCounter = 0;
export const makeFilterId = (): string => {
  idCounter += 1;
  return `filter-${idCounter}`;
};

export const filterDef = (key: FilterKey): FilterDef =>
  FILTER_DEFS.find((f) => f.key === key) ?? FILTER_DEFS[0];

// A property that exists in only one package (an unmatched slot). The whole
// property is a difference, so for the Difference filter it counts as differing
// on EVERY attribute — regardless of whether the present side's value happens to
// be populated. Without this, WI (always numeric) matches all such properties
// while Spud/Permit dates (often blank) match almost none.
const isUnmatched = (result: ComparisonResult): boolean =>
  result.matchStatus === 'unmatched_a' || result.matchStatus === 'unmatched_b';

// Economic rows that `buildAttributeRows` appends when economics is supplied but
// which are NOT offered as Difference options. Oil/Gas/Water OpEx and the
// LOE-per-well series are scenario/unit-level, so they differ uniformly across
// every property in a unit — not a useful per-property difference. Only the
// per-slot Total Target Value is filterable.
const HIDDEN_DIFFERENCE_KEYS = new Set<string>([
  'oilOpex',
  'gasOpex',
  'waterOpex',
  'loePerWell',
]);

// ── Option builders (for the modal) ──

export interface DifferenceOption {
  value: string;
  label: string;
  count: number;
}

/**
 * Difference-filter options: the attribute rows that differ for at least one
 * property, in the card/table row order, each with its differing-property count.
 * When `economics` is supplied, the per-slot Total Target Value row is included
 * (the other economic rows are excluded — see HIDDEN_DIFFERENCE_KEYS). A property
 * present in only one package counts toward every attribute's count.
 * @param {ComparisonResult[]} results - The comparison results
 * @param {EconomicsPair | null} economics - Both sides' economics, or null
 * @return {DifferenceOption[]} The difference options
 */
export const buildDifferenceOptions = (
  results: ComparisonResult[],
  economics: EconomicsPair | null = null
): DifferenceOption[] => {
  if (results.length === 0) return [];
  const order = buildAttributeRows(results[0], economics)
    .filter((row) => !HIDDEN_DIFFERENCE_KEYS.has(row.key))
    .map((row) => ({ key: row.key, label: row.label }));
  const counts = new Map<string, number>();
  results.forEach((r) => {
    const unmatched = isUnmatched(r);
    buildAttributeRows(r, economics).forEach((row) => {
      if (HIDDEN_DIFFERENCE_KEYS.has(row.key)) return;
      if (unmatched || row.differs) {
        counts.set(row.key, (counts.get(row.key) ?? 0) + 1);
      }
    });
  });
  return order
    .filter((o) => (counts.get(o.key) ?? 0) > 0)
    .map((o) => ({
      value: o.key,
      label: o.label,
      count: counts.get(o.key) ?? 0,
    }));
};

// Categorical value options for a filter key.
export const buildCategoricalOptions = (
  results: ComparisonResult[],
  key: FilterKey
): { value: string; label: string }[] =>
  categoricalAccessor(key).options(results);

// ── Application ──

const passesFilter = (
  result: ComparisonResult,
  filter: PropertyFilter,
  economics: EconomicsPair | null
): boolean => {
  if (filter.kind === 'difference') {
    // A property in only one package is a difference on every field, so it passes
    // any Difference filter (matches the count in buildDifferenceOptions).
    if (isUnmatched(result)) return true;
    const rows = buildAttributeRows(result, economics);
    return (filter.values ?? []).some(
      (k) => rows.find((row) => row.key === k)?.differs
    );
  }
  // categorical
  const values = categoricalAccessor(filter.key).values(result);
  return (filter.values ?? []).some((v) => values.includes(v));
};

// A filter only narrows results once the user has picked values.
const isFilterActive = (filter: PropertyFilter): boolean =>
  filter.enabled && (filter.values?.length ?? 0) > 0;

/**
 * Narrows the properties to those passing every active filter (AND).
 * @param {ComparisonResult[]} results - The comparison results
 * @param {PropertyFilter[]} filters - The active/staged filters
 * @param {EconomicsPair | null} economics - Both sides' economics, or null
 * @return {ComparisonResult[]} The filtered results
 */
export const applyPropertyFilters = (
  results: ComparisonResult[],
  filters: PropertyFilter[],
  economics: EconomicsPair | null = null
): ComparisonResult[] => {
  const active = filters.filter(isFilterActive);
  if (active.length === 0) return results;
  return results.filter((r) =>
    active.every((f) => passesFilter(r, f, economics))
  );
};

export interface FilterSummary {
  chips: string[];
}

/**
 * A sidebar-friendly summary of a filter: its selected value chips.
 * @param {PropertyFilter} filter - The filter to summarize
 * @param {ComparisonResult[]} results - The comparison results (for difference labels)
 * @param {EconomicsPair | null} economics - Both sides' economics, or null
 * @return {FilterSummary} The value chips
 */
export const summarizeFilter = (
  filter: PropertyFilter,
  results: ComparisonResult[],
  economics: EconomicsPair | null = null
): FilterSummary => {
  if (filter.kind === 'difference') {
    const labels = new Map(
      buildDifferenceOptions(results, economics).map((o) => [o.value, o.label])
    );
    return { chips: (filter.values ?? []).map((v) => labels.get(v) ?? v) };
  }
  return {
    chips: (filter.values ?? []).map((v) =>
      categoricalAccessor(filter.key).label(v)
    ),
  };
};
